import React, { useState } from 'react';
import { Users, Coins, UserPlus, Mail, Phone, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';

const MEMBERSHIP_TIERS = {
    Essential: { credits: 15, price: '£499/year' },
    Premium: { credits: 30, price: '£995/year' }
};

export function AdminMembersSection({ 
    members, 
    onAdjustCredits, 
    onActivateMembership,
    onCreateMember,
    createLoading = false
}) {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newMember, setNewMember] = useState({
        email: '',
        name: '',
        phone: '',
        membership_tier: 'Premium',
        initial_credits: 30,
        send_welcome_email: true
    });

    const handleTierChange = (tier) => {
        setNewMember(prev => ({
            ...prev,
            membership_tier: tier,
            initial_credits: MEMBERSHIP_TIERS[tier].credits
        }));
    };

    const handleCreateMember = async () => {
        if (!newMember.email || !newMember.name) return;
        
        const success = await onCreateMember(newMember);
        if (success) {
            setShowCreateModal(false);
            setNewMember({
                email: '',
                name: '',
                phone: '',
                membership_tier: 'Premium',
                initial_credits: 30,
                send_welcome_email: true
            });
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-members">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold">Members Management</h2>
                    <p className="text-muted-foreground text-sm mt-1">
                        {members.length} registered members
                    </p>
                </div>
                <Button 
                    onClick={() => setShowCreateModal(true)}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="add-member-btn"
                >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Member
                </Button>
            </div>
            
            <div className="space-y-3">
                {members.map(member => (
                    <div key={member.id} className="card-base" data-testid={`member-card-${member.id}`}>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                    <p className="font-bold text-lg">{member.name}</p>
                                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                                        member.profile?.membership_status === 'active' 
                                            ? 'bg-emerald-100 text-emerald-700' 
                                            : 'bg-gray-100 text-gray-600'
                                    }`}>
                                        {member.profile?.membership_status === 'active' ? 'Active' : 'Inactive'}
                                    </span>
                                    {member.profile?.membership_tier && (
                                        <span className="text-xs px-2 py-1 rounded-full bg-teal-100 text-teal-700">
                                            {member.profile.membership_tier}
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-muted-foreground">{member.email}</p>
                                <p className="text-xs text-primary">{member.membership_number}</p>
                                
                                <div className="mt-3 flex items-center gap-4">
                                    <div className="flex items-center gap-2 bg-primary/10 px-3 py-2 rounded-lg">
                                        <Coins className="w-5 h-5 text-primary" />
                                        <span className="font-bold text-lg">{member.profile?.credits_balance || 0}</span>
                                        <span className="text-sm text-muted-foreground">credits</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => onAdjustCredits(member)}
                                    data-testid={`adjust-credits-${member.id}`}
                                >
                                    <Coins className="w-4 h-4 mr-2" />
                                    Adjust Credits
                                </Button>
                                {member.profile?.membership_status !== 'active' && (
                                    <Button 
                                        size="sm"
                                        onClick={() => onActivateMembership(member.id)}
                                        data-testid={`activate-member-${member.id}`}
                                    >
                                        Activate Membership
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
                
                {members.length === 0 && (
                    <div className="card-base text-center py-12">
                        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground mb-4">No members registered yet</p>
                        <Button onClick={() => setShowCreateModal(true)}>
                            <UserPlus className="w-4 h-4 mr-2" />
                            Add First Member
                        </Button>
                    </div>
                )}
            </div>

            {/* Create Member Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Add New Member</DialogTitle>
                        <DialogDescription>
                            Create a new resident account. They'll receive a welcome email with login details.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="name">Full Name *</Label>
                            <Input
                                id="name"
                                value={newMember.name}
                                onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="e.g., Elizabeth Windsor"
                                data-testid="new-member-name"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="email">Email Address *</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="email"
                                    type="email"
                                    value={newMember.email}
                                    onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
                                    placeholder="email@example.com"
                                    className="pl-10"
                                    data-testid="new-member-email"
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="phone">Phone Number (Optional)</Label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="phone"
                                    type="tel"
                                    value={newMember.phone}
                                    onChange={(e) => setNewMember(prev => ({ ...prev, phone: e.target.value }))}
                                    placeholder="+44 7700 900000"
                                    className="pl-10"
                                    data-testid="new-member-phone"
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Membership Tier</Label>
                            <Select 
                                value={newMember.membership_tier} 
                                onValueChange={handleTierChange}
                            >
                                <SelectTrigger data-testid="membership-tier-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Essential">
                                        Essential - 15 credits ({MEMBERSHIP_TIERS.Essential.price})
                                    </SelectItem>
                                    <SelectItem value="Premium">
                                        Premium - 30 credits ({MEMBERSHIP_TIERS.Premium.price})
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="credits">Starting Credits</Label>
                            <Input
                                id="credits"
                                type="number"
                                min="0"
                                value={newMember.initial_credits}
                                onChange={(e) => setNewMember(prev => ({ ...prev, initial_credits: parseInt(e.target.value) || 0 }))}
                                data-testid="new-member-credits"
                            />
                            <p className="text-xs text-muted-foreground">
                                Default: {MEMBERSHIP_TIERS[newMember.membership_tier].credits} credits for {newMember.membership_tier} tier
                            </p>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg">
                            <div>
                                <p className="font-medium text-sm">Send Welcome Email</p>
                                <p className="text-xs text-muted-foreground">
                                    Include login credentials and getting started info
                                </p>
                            </div>
                            <Switch
                                checked={newMember.send_welcome_email}
                                onCheckedChange={(checked) => setNewMember(prev => ({ ...prev, send_welcome_email: checked }))}
                                data-testid="send-welcome-email-toggle"
                            />
                        </div>
                    </div>
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleCreateMember}
                            disabled={!newMember.email || !newMember.name || createLoading}
                            className="bg-teal-600 hover:bg-teal-700"
                            data-testid="create-member-btn"
                        >
                            {createLoading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    <UserPlus className="w-4 h-4 mr-2" />
                                    Create Member
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
